import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck, Shield, Play, BookOpen, Plus, Search, Filter,
  MoreHorizontal, Edit, Trash2, Copy, CheckCircle2, XCircle,
  Loader2, ChevronRight, Factory, Tag, Clock, Users, Zap,
  FileText, Settings2, Download, Upload, Archive, ToggleLeft,
  ToggleRight, Building2, AlertTriangle, Star, Layers, Hash
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const TYPE_CONFIG = {
  assessment: { label: "Assessment", icon: ClipboardCheck, color: "bg-violet-50 text-violet-700 border-violet-200", accent: "from-violet-600 to-purple-700" },
  compliance: { label: "Compliance", icon: Shield, color: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "from-emerald-600 to-teal-700" },
  ilt: { label: "ILT Session", icon: Play, color: "bg-amber-50 text-amber-700 border-amber-200", accent: "from-amber-600 to-orange-700" },
  course: { label: "Course", icon: BookOpen, color: "bg-blue-50 text-blue-700 border-blue-200", accent: "from-blue-600 to-indigo-700" },
};

const INDUSTRIES = ["General", "Manufacturing", "Construction", "Oil & Gas", "Automotive", "Pharmaceutical", "Food & Beverage", "Chemical", "Aerospace", "Electronics", "Healthcare", "Logistics", "Mining"];
const CATEGORIES = ["General", "Safety", "Compliance", "Quality", "Operations", "Technical Skills", "Leadership", "Environmental", "HR & Onboarding", "Maintenance"];

const TemplatesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");
  const [search, setSearch] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("all");

  // Create/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    template_type: "assessment", name: "", description: "", category: "General",
    industry: "General", tags: "", is_active: true, config: {},
  });

  // Clone dialog
  const [showClone, setShowClone] = useState(false);
  const [cloneTemplate, setCloneTemplate] = useState(null);
  const [cloneTenantId, setCloneTenantId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [cloning, setCloning] = useState(false);

  // Config editor
  const [configTab, setConfigTab] = useState("visual");

  const fetchTemplates = async () => {
    try {
      const params = {};
      if (activeType !== "all") params.template_type = activeType;
      if (filterIndustry !== "all") params.industry = filterIndustry;
      const res = await api.get("/master-templates", { params });
      setTemplates(res.data);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const r = await api.get("/master-templates/stats"); setStats(r.data); } catch {}
  };

  const fetchTenants = async () => {
    try { const r = await api.get("/tenants"); setTenants(r.data || []); } catch {}
  };

  useEffect(() => { fetchTemplates(); fetchStats(); fetchTenants(); }, [activeType, filterIndustry]);

  const filtered = templates.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase()) ||
    t.tags?.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
  );

  const openCreate = (type) => {
    setEditTemplate(null);
    setForm({ template_type: type || "assessment", name: "", description: "", category: "General", industry: "General", tags: "", is_active: true, config: {} });
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setForm({
      template_type: t.template_type, name: t.name, description: t.description || "",
      category: t.category || "General", industry: t.industry || "General",
      tags: (t.tags || []).join(", "), is_active: t.is_active !== false, config: t.config || {},
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (editTemplate) {
        await api.put(`/master-templates/${editTemplate.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/master-templates", payload);
        toast.success("Template created");
      }
      setShowDialog(false);
      fetchTemplates();
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template permanently?")) return;
    try { await api.delete(`/master-templates/${id}`); toast.success("Deleted"); fetchTemplates(); fetchStats(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleToggleActive = async (t) => {
    try {
      await api.put(`/master-templates/${t.id}`, { is_active: !t.is_active });
      toast.success(t.is_active ? "Deactivated" : "Activated");
      fetchTemplates(); fetchStats();
    } catch { toast.error("Failed"); }
  };

  const handleClone = async () => {
    if (!cloneTenantId) { toast.error("Select a tenant"); return; }
    setCloning(true);
    try {
      const r = await api.post(`/master-templates/${cloneTemplate.id}/clone`, { tenant_id: cloneTenantId });
      toast.success(`Cloned to tenant — ${r.data.type} created`);
      setShowClone(false);
      setCloneTenantId("");
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
    finally { setCloning(false); }
  };

  const updateConfig = (key, value) => {
    setForm(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
  };

  // ── Config editor for each template type ──
  const renderConfigEditor = () => {
    const cfg = form.config || {};
    const type = form.template_type;

    if (type === "assessment") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Time Limit (min)</Label><Input type="number" value={cfg.time_limit_minutes || 30} onChange={e => updateConfig("time_limit_minutes", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Passing Score (%)</Label><Input type="number" value={cfg.passing_score || 70} onChange={e => updateConfig("passing_score", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Max Attempts</Label><Input type="number" value={cfg.max_attempts || 3} onChange={e => updateConfig("max_attempts", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Difficulty</Label>
            <Select value={cfg.difficulty_level || "intermediate"} onValueChange={v => updateConfig("difficulty_level", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem><SelectItem value="expert">Expert</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.shuffle_questions !== false} onChange={e => updateConfig("shuffle_questions", e.target.checked)} className="rounded" /> Shuffle questions</label>
      </div>
    );

    if (type === "compliance") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Framework</Label><Input value={cfg.framework || ""} onChange={e => updateConfig("framework", e.target.value)} placeholder="e.g., ISO 9001:2015" className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Standard Code</Label><Input value={cfg.standard_code || ""} onChange={e => updateConfig("standard_code", e.target.value)} placeholder="e.g., ISO-9001" className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Due Days</Label><Input type="number" value={cfg.due_days || 30} onChange={e => updateConfig("due_days", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Recurrence</Label>
            <Select value={cfg.recurrence || "annual"} onValueChange={v => updateConfig("recurrence", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="semi-annual">Semi-Annual</SelectItem><SelectItem value="annual">Annual</SelectItem><SelectItem value="biennial">Biennial</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Renewal Grace Days</Label><Input type="number" value={cfg.renewal_grace_days || 14} onChange={e => updateConfig("renewal_grace_days", parseInt(e.target.value))} className="h-8 text-sm" /></div>
        </div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.is_mandatory !== false} onChange={e => updateConfig("is_mandatory", e.target.checked)} className="rounded" /> Mandatory compliance</label>
        <div>
          <Label className="text-xs">Checklist Items (one per line)</Label>
          <Textarea value={(cfg.checklist_items || []).join("\n")} onChange={e => updateConfig("checklist_items", e.target.value.split("\n").filter(Boolean))} rows={3} className="text-sm" placeholder="Quality manual updated&#10;Internal audit completed" />
        </div>
      </div>
    );

    if (type === "ilt") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Session Format</Label>
            <Select value={cfg.session_format || "classroom"} onValueChange={v => updateConfig("session_format", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="classroom">Classroom</SelectItem><SelectItem value="hands-on">Hands-On</SelectItem><SelectItem value="virtual">Virtual</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem><SelectItem value="workshop">Workshop</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Duration (hours)</Label><Input type="number" value={cfg.default_duration_hours || 2} onChange={e => updateConfig("default_duration_hours", parseFloat(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Default Capacity</Label><Input type="number" value={cfg.default_capacity || 30} onChange={e => updateConfig("default_capacity", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Location Type</Label>
            <Select value={cfg.location_type || "physical"} onValueChange={v => updateConfig("location_type", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="physical">Physical</SelectItem><SelectItem value="virtual">Virtual</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.assessment_required || false} onChange={e => updateConfig("assessment_required", e.target.checked)} className="rounded" /> Assessment required</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.certificate_on_completion || false} onChange={e => updateConfig("certificate_on_completion", e.target.checked)} className="rounded" /> Certificate on completion</label>
        </div>
        <div>
          <Label className="text-xs">Materials Checklist (one per line)</Label>
          <Textarea value={(cfg.materials_checklist || []).join("\n")} onChange={e => updateConfig("materials_checklist", e.target.value.split("\n").filter(Boolean))} rows={3} className="text-sm" placeholder="Safety handbook&#10;PPE kit&#10;Sign-in sheet" />
        </div>
      </div>
    );

    if (type === "course") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Course Type</Label>
            <Select value={cfg.course_type || "scorm_1.2"} onValueChange={v => updateConfig("course_type", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="scorm_1.2">SCORM 1.2</SelectItem><SelectItem value="scorm_2004">SCORM 2004</SelectItem><SelectItem value="ilt">ILT</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="document">Document</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Duration (hours)</Label><Input type="number" step="0.5" value={cfg.duration_hours || 1} onChange={e => updateConfig("duration_hours", parseFloat(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Difficulty</Label>
            <Select value={cfg.difficulty_level || "intermediate"} onValueChange={v => updateConfig("difficulty_level", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem><SelectItem value="expert">Expert</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Access Model</Label>
            <Select value={cfg.access_model || "subscription"} onValueChange={v => updateConfig("access_model", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="subscription">Subscription</SelectItem><SelectItem value="purchasable">Purchasable</SelectItem><SelectItem value="free">Free</SelectItem><SelectItem value="restricted">Restricted</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Passing Score (%)</Label><Input type="number" value={cfg.passing_score || 70} onChange={e => updateConfig("passing_score", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Max Attempts (0=unlimited)</Label><Input type="number" value={cfg.max_attempts || 0} onChange={e => updateConfig("max_attempts", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Expiry (months, 0=never)</Label><Input type="number" value={cfg.expiry_months || 0} onChange={e => updateConfig("expiry_months", parseInt(e.target.value))} className="h-8 text-sm" /></div>
          <div><Label className="text-xs">Completion Criteria</Label>
            <Select value={cfg.completion_criteria || "progress"} onValueChange={v => updateConfig("completion_criteria", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="progress">Progress</SelectItem><SelectItem value="score">Min Score</SelectItem><SelectItem value="manual">Manual</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.certificate_enabled || false} onChange={e => updateConfig("certificate_enabled", e.target.checked)} className="rounded" /> Certificate enabled</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.self_enrollment_enabled !== false} onChange={e => updateConfig("self_enrollment_enabled", e.target.checked)} className="rounded" /> Self-enrollment</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.renewal_required || false} onChange={e => updateConfig("renewal_required", e.target.checked)} className="rounded" /> Renewal required</label>
        </div>
      </div>
    );

    return null;
  };

  return (
    <AdminLayout title="Template Management">
      <div className="space-y-5 max-w-7xl" data-testid="templates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="templates-title">Template Management</h1>
            <p className="text-sm text-muted-foreground">Create master templates for assessments, compliance, ILT sessions, and courses</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-1.5 h-8 text-xs" data-testid="create-template-btn"><Plus className="w-3.5 h-3.5" /> Create Template</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                <DropdownMenuItem key={key} onClick={() => openCreate(key)} data-testid={`create-${key}-btn`}>
                  <cfg.icon className="w-4 h-4 mr-2" /> {cfg.label} Template
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Stats Strip */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card className="border shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center"><Layers className="w-4 h-4 text-primary" /></div>
                <div><p className="text-lg font-bold leading-none">{stats.total}</p><p className="text-[10px] text-muted-foreground">Total Templates</p></div>
              </CardContent>
            </Card>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <Card key={key} className="border shadow-none">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", cfg.color)}><cfg.icon className="w-4 h-4" /></div>
                  <div>
                    <p className="text-lg font-bold leading-none">{stats[key]?.total || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{cfg.label} <span className="text-green-600">({stats[key]?.cloned || 0} cloned)</span></p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Type Tabs + Filters */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 border-b overflow-x-auto">
            <button onClick={() => setActiveType("all")} className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors", activeType === "all" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")} data-testid="tab-all">
              All ({templates.length})
            </button>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => setActiveType(key)} className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-1.5", activeType === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")} data-testid={`tab-${key}`}>
                <cfg.icon className="w-3.5 h-3.5" /> {cfg.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs w-48" data-testid="search-templates" />
            </div>
            <Select value={filterIndustry} onValueChange={setFilterIndustry}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border shadow-none">
            <CardContent className="py-16 text-center">
              <Layers className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-sm font-medium">No templates found</p>
              <p className="text-xs text-muted-foreground mt-1">Create your first master template to get started</p>
              <Button size="sm" className="mt-4 gap-1.5 text-xs" onClick={() => openCreate(activeType !== "all" ? activeType : "assessment")} data-testid="empty-create-btn">
                <Plus className="w-3.5 h-3.5" /> Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            <AnimatePresence>
              {filtered.map((t, i) => {
                const tc = TYPE_CONFIG[t.template_type] || TYPE_CONFIG.assessment;
                const TypeIcon = tc.icon;
                return (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}>
                    <Card className="border shadow-none h-full group hover:border-primary/30 transition-colors" data-testid={`template-card-${t.id}`}>
                      <CardContent className="p-0">
                        <div className={cn("h-2 rounded-t-lg bg-gradient-to-r", tc.accent)} />
                        <div className="p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", tc.color)}>
                                <TypeIcon className="w-5 h-5" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold line-clamp-1">{t.name}</h3>
                                <p className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">{t.description || "No description"}</p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`template-actions-${t.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onClick={() => openEdit(t)}><Edit className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setCloneTemplate(t); setShowClone(true); }}><Copy className="w-3.5 h-3.5 mr-2" />Clone to Tenant</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleActive(t)}>
                                  {t.is_active ? <><XCircle className="w-3.5 h-3.5 mr-2" />Deactivate</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-2" />Activate</>}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(t.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className={cn("text-[9px] px-1.5 py-0 border gap-0.5", tc.color)}><TypeIcon className="w-2.5 h-2.5" />{tc.label}</Badge>
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0">{t.category}</Badge>
                            {t.industry && t.industry !== "General" && <Badge variant="outline" className="text-[9px] px-1.5 py-0 gap-0.5"><Factory className="w-2.5 h-2.5" />{t.industry}</Badge>}
                            {!t.is_active && <Badge className="text-[9px] px-1.5 py-0 bg-red-50 text-red-600 border-red-200">Inactive</Badge>}
                          </div>

                          {(t.tags || []).length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              {t.tags.slice(0, 4).map(tag => (
                                <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>
                              ))}
                              {t.tags.length > 4 && <span className="text-[9px] text-muted-foreground">+{t.tags.length - 4}</span>}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-2 border-t text-[10px] text-muted-foreground">
                            <span className="flex items-center gap-1"><Copy className="w-3 h-3" />{t.clone_count || 0} cloned</span>
                            <span>{new Date(t.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── CREATE/EDIT DIALOG ── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">{editTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editTemplate && (
              <div>
                <Label className="text-xs">Template Type</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <button key={key} onClick={() => setForm({ ...form, template_type: key, config: {} })}
                      className={cn("p-2.5 rounded-lg border-2 text-center transition-all", form.template_type === key ? "border-[#1e3a5f] bg-[#1e3a5f]/5 shadow-sm" : "border-muted hover:border-[#1e3a5f]/30")}
                      data-testid={`type-select-${key}`}>
                      <cfg.icon className={cn("w-5 h-5 mx-auto mb-1", form.template_type === key ? "text-[#1e3a5f]" : "text-muted-foreground")} />
                      <span className={cn("text-[10px] font-medium", form.template_type === key ? "text-[#1e3a5f]" : "text-muted-foreground")}>{cfg.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Template name" className="h-8 text-sm" data-testid="template-name-input" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" placeholder="Brief description" /></div>
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Industry</Label>
                <Select value={form.industry} onValueChange={v => setForm({ ...form, industry: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label className="text-xs">Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className="h-8 text-sm" placeholder="safety, osha, manufacturing" /></div>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-xs font-semibold mb-3 flex items-center gap-1.5"><Settings2 className="w-3.5 h-3.5" /> {TYPE_CONFIG[form.template_type]?.label} Configuration</h4>
              {renderConfigEditor()}
            </div>

            <label className="flex items-center gap-2 text-xs p-2.5 rounded-lg border">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
              <span className="font-medium">Active — available for cloning to tenants</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} {editTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CLONE DIALOG ── */}
      <Dialog open={showClone} onOpenChange={setShowClone}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Clone Template to Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cloneTemplate && (
              <div className="p-3 rounded-lg border flex items-center gap-3">
                {(() => { const cfg = TYPE_CONFIG[cloneTemplate.template_type]; const Icon = cfg.icon; return <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", cfg.color)}><Icon className="w-5 h-5" /></div>; })()}
                <div><p className="text-sm font-semibold">{cloneTemplate.name}</p><p className="text-[10px] text-muted-foreground">{TYPE_CONFIG[cloneTemplate.template_type]?.label}</p></div>
              </div>
            )}
            <div>
              <Label className="text-xs">Select Tenant</Label>
              <Select value={cloneTenantId} onValueChange={setCloneTenantId}>
                <SelectTrigger className="h-9 text-sm" data-testid="clone-tenant-select"><SelectValue placeholder="Choose a tenant..." /></SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name || t.company_name || t.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-muted-foreground">This will create a new {cloneTemplate?.template_type} in the selected tenant's workspace using this template's configuration. The tenant can then customize it.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowClone(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleClone} disabled={cloning} data-testid="clone-confirm-btn">
              {cloning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />} Clone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TemplatesPage;
